/**
 * A service that provides operations for interfacing with the javascript keycloak adapter
 */
export class KeycloakService {
  constructor(private $q: ng.IQService, private keycloakInstance) {
  }

  /**
   * Function to retrieve the JSON token for the current user
   * @returns the JSON token for the current user
   */
  public getCurrentToken() {
    return this.keycloakInstance.token;
  }

  /**
   * Function to get the the realm access for the current user
   * @returns an object that describes the realm access for the current user
   */
  public getCurrentRealmAccess() {
    return this.keycloakInstance.realmAccess;
  }

  /**
   * Function that performs a logout on the current user
   * @param options logout options param
   * @returns a promise that resolves once the logout operation has completed
   */
  public logout(options?) {
    const deferred = this.$q.defer();

    this.keycloakInstance.logout(options).success(() => {
      deferred.resolve();
    }).error(() => {
      deferred.reject();
    });

    return deferred.promise;
  }

  /**
   * Function that retrieves the current users profile
   * @returns a promise that resolves with the current users profile
   */
  public loadUserProfile() {
    const deferred = this.$q.defer();

    this.keycloakInstance.loadUserProfile().success((userProfile: InterACT.Interfaces.Users.User) => {
      deferred.resolve(userProfile);
    }).error(() => {
      deferred.reject();
    });

    return deferred.promise;
  }

  /**
   * Function that navigates to the account management screen for the current user
   * @returns a promise that resolve once the app has navigated to the account management section of the current user
   */
  public goToAccountManagement(): ng.IPromise<void> {
    const deferred = this.$q.defer<void>();

    this.keycloakInstance.accountManagement().success(() => {
      deferred.resolve();
    }).error(() => {
      deferred.reject();
    });

    return deferred.promise;
  }

  /**
   * Function that refreshes the current token if the token is valid for less than the supplied time
   * @param remainingTokenTime the time threshold in seconds. Tokens that expire in less time than is specified here will be renewed by this method.
   * @returns a promise that resolves with a boolean indicating if the token was renewed or not.
   */
  public refreshTokenIfRemainingTimeIsLessThan(remainingTokenTime): ng.IPromise<boolean> {
    const deferred = this.$q.defer<boolean>();

    this.keycloakInstance.updateToken(remainingTokenTime).success((wasRefreshed: boolean) => {
      deferred.resolve(wasRefreshed);
    }).error(() => {
      deferred.reject();
    });

    return deferred.promise;
  }

  /**
   * Determines whether the token has the given role for the given resource
   */
  public canManageUsers(): boolean {
    return this.keycloakInstance.hasResourceRole('manage-users', 'realm-management');
  }

  /**
   * Returns the URL to the Admin Console - note this is a workaround for now as there does not seem to be a convenience function
   * on the keycloak instance for redirecting to the keycloak admin console
   */
  public createAdminConsoleUrl(): string {
    let url = this.keycloakInstance.createAccountUrl();
    url = url.replace('realms', 'admin');
    url = url.replace('account', 'console');
    return url;
  }
}